"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="nav-link-hover relative font-default text-sm font-bold uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-6 w-32 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-6 w-24 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-6 w-28 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 mt-16">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-default text-xl font-bold uppercase tracking-widest text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="228341" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/50" : "border-b border-border"}`}>
      <style>{`
        @keyframes underline-vibrate {
          0% { transform: scaleX(0); }
          40% { transform: scaleX(1.05); }
          60% { transform: scaleX(0.95); }
          80% { transform: scaleX(1.02); }
          100% { transform: scaleX(1); }
        }
        .nav-link-hover::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          height: 2px;
          width: 100%;
          background-color: var(--primary);
          transform-origin: center;
          transform: scaleX(0);
          transition: transform 0.3s ease-out;
        }
        .nav-link-hover:hover::after {
          animation: underline-vibrate 0.4s ease-out forwards;
        }
      `}</style>

      <div className="container mx-auto px-4 h-20 flex items-center">
        
        <div className="flex flex-1 justify-start md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-r border-border w-[300px] sm:w-[400px]">
              <MenuProvider menu_id="16">
                <MobileMenu />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>

        
        <div className="flex justify-center md:justify-start md:mr-12">
          <Link to="/HomePage#">
            <img src="https://causal-crawfish.10web.cloud/wp-content/uploads/2026/05/klassentrtmnt.png" className="!w-[60px] !h-[60px]" alt="Atlantis Holding" />
          </Link>
        </div>

        
        <div className="hidden md:flex flex-1 justify-end">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        
        <div className="flex flex-1 md:hidden"></div>
      </div>
    </section>;
}